/* eslint-disable react-refresh/only-export-components */
import { useState } from 'react';
import { cn } from '@sglara/cn';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Input } from 'design-system/src/components/Input/Input';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Dropdown, DropdownItem, DropdownContainer } from '../../Dropdown';
import { FormLabel } from '../../FormLabel';
import FileUploadField from './FileUploadField';
import type { NewCommentFile, FileType } from '../../../api';

export type EntryMethod = 'upload' | 'manual';

export type InsuranceFormData = {
  insuranceCompanyName: string;
  insuranceType: string;
  cardholderId: string;
  rxBin: string;
  pcn: string;
  groupId: string;
};

export const INITIAL_INSURANCE_FORM_DATA: InsuranceFormData = {
  insuranceCompanyName: '',
  insuranceType: '',
  cardholderId: '',
  rxBin: '',
  pcn: '',
  groupId: '',
};

export type InsuranceInputProps = {
  entryMethod: EntryMethod;
  onEntryMethodChange: (method: EntryMethod) => void;
  uploadedFiles: NewCommentFile[];
  onFilesChange: (files: NewCommentFile[]) => void;
  formData: InsuranceFormData;
  onFormDataChange: (data: InsuranceFormData) => void;
  fieldErrors: Partial<Record<keyof InsuranceFormData, boolean>>;
  onFieldErrorsChange: (errors: Partial<Record<keyof InsuranceFormData, boolean>>) => void;
  /** Whether to show a validation error on the upload field (e.g. when submit is attempted with no files) */
  showUploadError?: boolean;
};

const INSURANCE_TYPES = ['Medicare', 'Medicaid', 'Commercial', 'Government', 'Other'] as const;

const isBinRequired = (formData: InsuranceFormData): boolean =>
  formData.insuranceType !== '' && formData.insuranceType !== 'Medicaid';

const isBinValid = (formData: InsuranceFormData): boolean =>
  !isBinRequired(formData) || formData.rxBin.trim().length === 6;

// Shared class name patterns
const OPTIONAL_LABEL_CLASSES = 'text-grayscale-500 text-body-extra-small-space-grotesk font-normal';
const LABEL_ROW_CLASSES = 'flex items-baseline gap-1.5 font-space-grotesk';
const FIELD_CONTAINER_CLASSES = 'flex flex-col';
const TWO_COLUMN_ROW_CLASSES = 'flex gap-4 [&>*]:flex-1';

type TabButtonProps = {
  id: string;
  active: boolean;
  onClick: () => void;
  controls: string;
  children: React.ReactNode;
};

const TabButton = ({ id, active, onClick, controls, children }: TabButtonProps) => (
  <button
    type="button"
    id={id}
    role="tab"
    aria-selected={active}
    aria-controls={controls}
    onClick={onClick}
    className={cn(
      'flex-1 cursor-pointer px-6 py-2.5',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2',
      active
        ? // Transparent border has been added for Windows high contrast mode, which removes background colors and box shadows but makes all borders 100% opacity black
          'm-0.5 rounded border border-solid border-transparent bg-white shadow-[0_0_2px_0_rgba(0,0,0,0.16)]'
        : // 1px more margin than active state to account for lack of transparent border
          'm-[3px] border-none bg-surface-200',
    )}
  >
    <Typography
      styledAs="bodySmallSpaceGrotesk"
      weight={500}
      color={active ? 'var(--grayscale-900)' : 'var(--grayscale-600)'}
    >
      {children}
    </Typography>
  </button>
);

export const InsuranceInput = ({
  entryMethod,
  onEntryMethodChange,
  uploadedFiles,
  onFilesChange,
  formData,
  onFormDataChange,
  fieldErrors,
  onFieldErrorsChange,
  showUploadError,
}: InsuranceInputProps) => {
  const [duplicateFileNameUploaded, setDuplicateFileNameUploaded] = useState<string | null>(null);

  const handleFileUpload = (newFiles: NewCommentFile[]) => {
    setDuplicateFileNameUploaded(null);

    // Check for duplicate filenames - we use filenames as keys when sending to server,
    // so duplicates would cause issues downstream
    let foundDuplicateFileName = false;
    for (const fileToUpload of newFiles) {
      const existingFile = uploadedFiles.find((f) => f.file.name === fileToUpload.file.name);
      if (existingFile) {
        setDuplicateFileNameUploaded(fileToUpload.file.name);
        foundDuplicateFileName = true;
        break;
      }
    }

    if (!foundDuplicateFileName) {
      onFilesChange([...uploadedFiles, ...newFiles]);
    }
  };

  const handleFileDelete = (filename: string, documentType: FileType) => {
    setDuplicateFileNameUploaded(null);
    onFilesChange(uploadedFiles.filter((f) => !(f.file.name === filename && f.document_type === documentType)));
  };

  const handleFormChange = (field: keyof InsuranceFormData, value: string) => {
    onFormDataChange({ ...formData, [field]: value });

    const updatedErrors = { ...fieldErrors };
    let hasUpdates = false;

    // Clear error when user starts typing
    if (fieldErrors[field] && value.trim() !== '') {
      updatedErrors[field] = false;
      hasUpdates = true;
    }

    // Clear rxBin error when insuranceType changes to Medicaid
    if (field === 'insuranceType' && (value === '' || value === 'Medicaid') && fieldErrors.rxBin) {
      updatedErrors.rxBin = false;
      hasUpdates = true;
    }

    if (hasUpdates) {
      onFieldErrorsChange(updatedErrors);
    }
  };

  const handleEntryMethodChange = (method: EntryMethod) => {
    onEntryMethodChange(method);
    onFieldErrorsChange({});
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Tab Toggle */}
      <div
        role="tablist"
        aria-label="Insurance entry method"
        className="flex w-full overflow-hidden rounded-medium bg-surface-200"
      >
        <TabButton
          id="tab-upload"
          active={entryMethod === 'upload'}
          onClick={() => handleEntryMethodChange('upload')}
          controls="tabpanel-upload"
        >
          Upload insurance card
        </TabButton>
        <TabButton
          id="tab-manual"
          active={entryMethod === 'manual'}
          onClick={() => handleEntryMethodChange('manual')}
          controls="tabpanel-manual"
        >
          Manually enter insurance
        </TabButton>
      </div>

      {entryMethod === 'upload' ? (
        /* Upload Section */
        <div id="tabpanel-upload" role="tabpanel" aria-labelledby="tab-upload" className="mt-3">
          <FileUploadField
            instructions="Please upload images of the front and back of the insurance card."
            uploadedFiles={uploadedFiles}
            onFileDelete={handleFileDelete}
            onFilesUpload={handleFileUpload}
            fileTypesOptions={['Insurance Card Front', 'Insurance Card Back']}
            errors={[
              {
                message: 'Please upload an insurance card image.',
                visible: !!showUploadError,
              },
              {
                message: `A file with the name "${duplicateFileNameUploaded}" has already been added. Please rename the file and try again.`,
                visible: !!duplicateFileNameUploaded,
              },
            ]}
          />
        </div>
      ) : (
        /* Manual Entry Form */
        <div id="tabpanel-manual" role="tabpanel" aria-labelledby="tab-manual" className="flex flex-col gap-4">
          <div className={TWO_COLUMN_ROW_CLASSES}>
            <div className={FIELD_CONTAINER_CLASSES}>
              <FormLabel htmlFor="insurance-company-name">
                <span className={LABEL_ROW_CLASSES}>
                  Insurance Name
                  <span className={OPTIONAL_LABEL_CLASSES}>Optional</span>
                </span>
              </FormLabel>
              <Input
                id="insurance-company-name"
                label="Insurance Name"
                hideLabel
                value={formData.insuranceCompanyName}
                onChange={(e) => handleFormChange('insuranceCompanyName', (e.target as HTMLInputElement).value)}
                inputSize="sm"
              />
            </div>
            <DropdownContainer>
              <FormLabel id="insurance-type-label">Insurance Type</FormLabel>
              <Dropdown
                labelId="insurance-type-label"
                id="insurance-type"
                value={formData.insuranceType}
                onChange={(event) => handleFormChange('insuranceType', event.target.value as string)}
                displayEmpty
                renderValue={(value) => (value as string) || 'Select type'}
                $size="sm"
                $hasError={fieldErrors.insuranceType}
                aria-invalid={fieldErrors.insuranceType}
                aria-describedby={fieldErrors.insuranceType ? 'insurance-type-error' : undefined}
              >
                {INSURANCE_TYPES.map((type) => (
                  <DropdownItem key={type} value={type}>
                    {type}
                  </DropdownItem>
                ))}
              </Dropdown>
              {fieldErrors.insuranceType && (
                <ErrorMessage id="insurance-type-error">Insurance type is required</ErrorMessage>
              )}
            </DropdownContainer>
          </div>

          <div className={TWO_COLUMN_ROW_CLASSES}>
            <div className={FIELD_CONTAINER_CLASSES}>
              <FormLabel htmlFor="rx-bin">
                <span className={LABEL_ROW_CLASSES}>
                  RX BIN
                  {!isBinRequired(formData) && <span className={OPTIONAL_LABEL_CLASSES}>Optional</span>}
                </span>
              </FormLabel>
              <Input
                id="rx-bin"
                label="RX BIN"
                hideLabel
                value={formData.rxBin}
                onChange={(e) => handleFormChange('rxBin', (e.target as HTMLInputElement).value)}
                inputSize="sm"
                maxLength={6}
                hasError={fieldErrors.rxBin}
                aria-invalid={fieldErrors.rxBin}
                aria-describedby={fieldErrors.rxBin ? 'rx-bin-error' : undefined}
              />
              {fieldErrors.rxBin && <ErrorMessage id="rx-bin-error">RX BIN must be 6 characters</ErrorMessage>}
            </div>
            <div>
              <Input
                id="cardholder-id"
                label="Cardholder ID"
                value={formData.cardholderId}
                onChange={(e) => handleFormChange('cardholderId', (e.target as HTMLInputElement).value)}
                inputSize="sm"
                hasError={fieldErrors.cardholderId}
                aria-invalid={fieldErrors.cardholderId}
                aria-describedby={fieldErrors.cardholderId ? 'cardholder-id-error' : undefined}
              />
              {fieldErrors.cardholderId && (
                <ErrorMessage id="cardholder-id-error">Cardholder ID is required</ErrorMessage>
              )}
            </div>
          </div>

          <div className={TWO_COLUMN_ROW_CLASSES}>
            <div className={FIELD_CONTAINER_CLASSES}>
              <FormLabel htmlFor="group-id">
                <span className={LABEL_ROW_CLASSES}>
                  RX Group #<span className={OPTIONAL_LABEL_CLASSES}>Optional</span>
                </span>
              </FormLabel>
              <Input
                id="group-id"
                label="RX Group #"
                hideLabel
                value={formData.groupId}
                onChange={(e) => handleFormChange('groupId', (e.target as HTMLInputElement).value)}
                inputSize="sm"
              />
            </div>
            <div className={FIELD_CONTAINER_CLASSES}>
              <FormLabel htmlFor="pcn">
                <span className={LABEL_ROW_CLASSES}>
                  PCN #<span className={OPTIONAL_LABEL_CLASSES}>Optional</span>
                </span>
              </FormLabel>
              <Input
                id="pcn"
                label="PCN #"
                hideLabel
                value={formData.pcn}
                onChange={(e) => handleFormChange('pcn', (e.target as HTMLInputElement).value)}
                inputSize="sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper to validate insurance input based on entry method
 */
export const isInsuranceInputValid = (
  entryMethod: EntryMethod,
  uploadedFiles: NewCommentFile[],
  formData: InsuranceFormData,
): boolean => {
  if (entryMethod === 'upload') {
    return uploadedFiles.length > 0;
  }

  return formData.insuranceType !== '' && formData.cardholderId.trim() !== '' && isBinValid(formData);
};

/**
 * Helper to get validation errors for manual entry
 */
export const getManualEntryErrors = (
  formData: InsuranceFormData,
): Partial<Record<keyof InsuranceFormData, boolean>> => {
  const errors: Partial<Record<keyof InsuranceFormData, boolean>> = {};

  if (formData.insuranceType === '') {
    errors.insuranceType = true;
  }
  if (formData.cardholderId.trim() === '') {
    errors.cardholderId = true;
  }
  if (!isBinValid(formData)) {
    errors.rxBin = true;
  }

  return errors;
};
